// app/frontend/pages/Admin/Videos/Edit.tsx
import { useState } from "react";
import {
  Box,
  Stack,
  Group,
  Grid,
  Image,
  Badge,
  Paper,
  TagsInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useMediaQuery } from "@mantine/hooks";
import { SPACING, TEXT_SIZES, MOBILE_QUERY } from "../../../components/ui/spacing";
import {
  IconArrowLeft,
  IconDeviceFloppy,
  IconEye,
  IconEyeOff,
  IconExternalLink,
} from "@tabler/icons-react";
import { Link, router } from "@inertiajs/react";
import { L2KH2, L2KH5, L2KText, L2KCaption } from "../../../components/ui/Typography";
import {
  L2KInput,
  L2KTextarea,
  L2KSelect,
  L2KMultiSelect,
  L2KButton,
} from "../../../components/ui/Forms";
import { l2kNotify } from "../../../components/ui/Feedback";
import AdminLayout from "../../../components/layouts/AdminLayout";
import { SEO } from "../../../components/common/SEO";

interface Video {
  id: number;
  slug: string;
  title: string;
  description: string;
  thumbnail_url: string;
  duration: string;
  skill_level: string;
  published: boolean;
  vimeo_id: string;
  vimeo_url: string;
  vimeo_embed_url: string;
  categories: { id: number; name: string; slug: string }[];
  category_ids: number[];
  tags: string[];
  last_synced_at: string;
  created_at: string;
  updated_at: string;
}

interface Category {
  id: number;
  name: string;
  slug: string;
}

interface Props {
  video: Video;
  categories: Category[];
  skill_levels: string[];
}

export default function VideosEdit({ video, categories, skill_levels }: Props) {
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const isMobile = useMediaQuery(MOBILE_QUERY);

  const form = useForm({
    initialValues: {
      title: video.title || "",
      description: video.description || "",
      skill_level: video.skill_level || "",
      category_ids: video.category_ids.map(String) || [],
      tags: video.tags || [],
    },
  });

  const handleSave = async (values: typeof form.values) => {
    setSaving(true);
    try {
      const response = await fetch(`/admin/videos/${video.id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || "",
          Accept: "application/json",
        },
        body: JSON.stringify({
          video: {
            title: values.title,
            description: values.description,
            skill_level: values.skill_level,
            category_ids: values.category_ids.map(Number),
            tags: values.tags,
          },
        }),
      });

      const data = await response.json();
      if (data.success) {
        l2kNotify.success("Saved", "Video updated successfully");
      } else {
        l2kNotify.error("Error", data.errors?.join(", ") || "Failed to save video");
      }
    } catch (error) {
      console.error("Save error:", error);
      l2kNotify.error("Error", "Failed to save video");
    } finally {
      setSaving(false);
    }
  };

  const handlePublishToggle = async () => {
    setPublishing(true);
    const action = video.published ? "unpublish" : "publish";

    try {
      // Save form data first before publishing
      if (!video.published) {
        const saveResponse = await fetch(`/admin/videos/${video.id}`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || "",
            Accept: "application/json",
          },
          body: JSON.stringify({
            video: {
              title: form.values.title,
              description: form.values.description,
              skill_level: form.values.skill_level,
              category_ids: form.values.category_ids.map(Number),
              tags: form.values.tags,
            },
          }),
        });

        const saveData = await saveResponse.json();
        if (!saveData.success) {
          l2kNotify.error("Error", saveData.errors?.join(", ") || "Failed to save video");
          setPublishing(false);
          return;
        }
      }

      const response = await fetch(`/admin/videos/${video.id}/${action}`, {
        method: "POST",
        headers: {
          "X-CSRF-Token": document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || "",
          "Accept": "application/json",
        },
      });

      if (response.ok) {
        l2kNotify.success(
          video.published ? "Unpublished" : "Published",
          video.published ? "Video is now a draft" : "Video is now live"
        );
        router.reload();
      } else {
        const data = await response.json().catch(() => ({}));
        const errorMessage = data.errors?.join(", ") || `Failed to ${action} video`;
        l2kNotify.error("Cannot Publish", errorMessage);
      }
    } catch (error) {
      l2kNotify.error("Error", `Failed to ${action} video`);
    } finally {
      setPublishing(false);
    }
  };

  return (
    <>
      <SEO title={`Edit: ${video.title} - Admin - Learntokiz`} />
      <Stack gap={isMobile ? SPACING.stack.normal.mobile : SPACING.stack.loose.desktop}>
        {/* Header */}
        <Stack gap={isMobile ? "xs" : 0}>
          <Group justify="space-between" wrap={isMobile ? "wrap" : "nowrap"}>
            <Group gap={isMobile ? "xs" : "sm"}>
              <Link href="/admin/videos">
                <L2KButton
                  variant="ghost"
                  leftSection={<IconArrowLeft size={16} />}
                  size={isMobile ? TEXT_SIZES.button.mobile : TEXT_SIZES.button.desktop}
                >
                  Back
                </L2KButton>
              </Link>
              {!isMobile && (
                <Box>
                  <L2KH2 mb={0}>Edit Video</L2KH2>
                </Box>
              )}
            </Group>
            <Group gap={isMobile ? "xs" : "sm"}>
              <L2KButton
                variant={video.published ? "secondary" : "primary"}
                leftSection={video.published ? <IconEyeOff size={16} /> : <IconEye size={16} />}
                loading={publishing}
                onClick={handlePublishToggle}
                size={isMobile ? TEXT_SIZES.button.mobile : TEXT_SIZES.button.desktop}
              >
                {video.published ? "Unpublish" : "Publish"}
              </L2KButton>
              <L2KButton
                leftSection={<IconDeviceFloppy size={16} />}
                loading={saving}
                onClick={() => form.onSubmit(handleSave)()}
                size={isMobile ? TEXT_SIZES.button.mobile : TEXT_SIZES.button.desktop}
              >
                Save{!isMobile && " Changes"}
              </L2KButton>
            </Group>
          </Group>
          {isMobile && (
            <L2KH2 mb={0} size="h3">Edit Video</L2KH2>
          )}
        </Stack>

        <Grid gap={isMobile ? "sm" : "lg"}>
          {/* Main Form */}
          <Grid.Col span={{ base: 12, md: 8 }}>
            <Stack gap={isMobile ? SPACING.stack.normal.mobile : SPACING.stack.loose.desktop}>
              <Paper
                p={isMobile ? SPACING.card.mobile : SPACING.card.desktop}
                radius="md"
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.03)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                }}
              >
                <Stack gap={isMobile ? SPACING.form.mobile : SPACING.form.desktop}>
                  <L2KInput
                    label="Title"
                    placeholder="Video title"
                    size={isMobile ? TEXT_SIZES.input.mobile : TEXT_SIZES.input.desktop}
                    {...form.getInputProps("title")}
                  />
                  <L2KTextarea
                    label="Description"
                    placeholder="Video description"
                    minRows={isMobile ? 3 : 4}
                    size={isMobile ? TEXT_SIZES.input.mobile : TEXT_SIZES.input.desktop}
                    {...form.getInputProps("description")}
                  />
                  <Grid gap={isMobile ? "xs" : "md"}>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <L2KSelect
                        label="Skill Level"
                        placeholder="Select skill level"
                        data={skill_levels || ["beginner", "improver", "intermediate", "advanced"]}
                        size={isMobile ? TEXT_SIZES.input.mobile : TEXT_SIZES.input.desktop}
                        {...form.getInputProps("skill_level")}
                      />
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <L2KMultiSelect
                        label="Categories"
                        placeholder="Select categories"
                        data={categories.map((c) => ({
                          value: c.id.toString(),
                          label: c.name,
                        }))}
                        size={isMobile ? TEXT_SIZES.input.mobile : TEXT_SIZES.input.desktop}
                        {...form.getInputProps("category_ids")}
                      />
                    </Grid.Col>
                  </Grid>
                  <TagsInput
                    label="Tags"
                    placeholder="Add tags and press Enter"
                    size={isMobile ? TEXT_SIZES.input.mobile : TEXT_SIZES.input.desktop}
                    {...form.getInputProps("tags")}
                    styles={{
                      label: {
                        color: "#F0F0F0",
                        fontFamily: "'Public Sans', sans-serif",
                        fontWeight: 500,
                        marginBottom: isMobile ? 4 : 6,
                      },
                      input: {
                        backgroundColor: "rgba(255, 255, 255, 0.03)",
                        borderColor: "rgba(255, 152, 0, 0.2)",
                        color: "#F0F0F0",
                        "&:focus-within": {
                          borderColor: "#FF9800",
                        },
                      },
                      pill: {
                        backgroundColor: "rgba(255, 152, 0, 0.2)",
                        color: "#FF9800",
                      },
                    }}
                  />
                </Stack>
              </Paper>

              {/* Video Preview */}
              <Paper
                p={isMobile ? SPACING.card.mobile : SPACING.card.desktop}
                radius="md"
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.03)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                }}
              >
                <L2KH5 mb={isMobile ? "sm" : "md"}>Video Preview</L2KH5>
                <Box style={{ aspectRatio: "16/9", borderRadius: "8px", overflow: "hidden" }}>
                  {video.vimeo_embed_url ? (
                    <iframe
                      src={video.vimeo_embed_url}
                      style={{ width: "100%", height: "100%", border: "none" }}
                      allow="autoplay; fullscreen; picture-in-picture"
                      allowFullScreen
                    />
                  ) : (
                    <Box
                      style={{
                        width: "100%",
                        height: "100%",
                        backgroundColor: "rgba(255, 255, 255, 0.05)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <L2KText variant="tertiary">No video available</L2KText>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Stack>
          </Grid.Col>

          {/* Sidebar */}
          <Grid.Col span={{ base: 12, md: 4 }}>
            <Stack gap={isMobile ? SPACING.stack.normal.mobile : SPACING.stack.loose.desktop}>
              {/* Status Card */}
              <Paper
                p={isMobile ? SPACING.card.mobile : SPACING.card.desktop}
                radius="md"
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.03)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                }}
              >
                <L2KH5 mb={isMobile ? "sm" : "md"}>Status</L2KH5>
                <Stack gap={isMobile ? "xs" : "sm"}>
                  <Group justify="space-between">
                    <L2KCaption variant="tertiary">Published</L2KCaption>
                    <Badge color={video.published ? "green" : "gray"} size={isMobile ? "sm" : "md"}>
                      {video.published ? "Yes" : "No"}
                    </Badge>
                  </Group>
                  <Group justify="space-between">
                    <L2KCaption variant="tertiary">Created</L2KCaption>
                    <L2KCaption>{video.created_at}</L2KCaption>
                  </Group>
                  <Group justify="space-between">
                    <L2KCaption variant="tertiary">Updated</L2KCaption>
                    <L2KCaption>{video.updated_at}</L2KCaption>
                  </Group>
                  <Group justify="space-between">
                    <L2KCaption variant="tertiary">Last Synced</L2KCaption>
                    <L2KCaption>{video.last_synced_at || "Never"}</L2KCaption>
                  </Group>
                </Stack>
              </Paper>

              {/* Thumbnail Card */}
              <Paper
                p={isMobile ? SPACING.card.mobile : SPACING.card.desktop}
                radius="md"
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.03)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                }}
              >
                <L2KH5 mb={isMobile ? "sm" : "md"}>Thumbnail</L2KH5>
                <Image
                  src={video.thumbnail_url}
                  alt={video.title}
                  radius="md"
                  style={{ aspectRatio: "16/9" }}
                />
              </Paper>

              {/* Vimeo Info Card */}
              <Paper
                p={isMobile ? SPACING.card.mobile : SPACING.card.desktop}
                radius="md"
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.03)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                }}
              >
                <L2KH5 mb={isMobile ? "sm" : "md"}>Vimeo Info</L2KH5>
                <Stack gap={isMobile ? "xs" : "sm"}>
                  <Group justify="space-between">
                    <L2KCaption variant="tertiary">Vimeo ID</L2KCaption>
                    <L2KCaption>{video.vimeo_id || "—"}</L2KCaption>
                  </Group>
                  <Group justify="space-between">
                    <L2KCaption variant="tertiary">Duration</L2KCaption>
                    <L2KCaption>{video.duration || "—"}</L2KCaption>
                  </Group>
                  {video.vimeo_url && (
                    <a
                      href={video.vimeo_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "#FF9800", display: "flex", alignItems: "center", gap: "4px" }}
                    >
                      <L2KCaption variant="accent">View on Vimeo</L2KCaption>
                      <IconExternalLink size={14} />
                    </a>
                  )}
                </Stack>
              </Paper>

              {/* URL Card */}
              <Paper
                p={isMobile ? SPACING.card.mobile : SPACING.card.desktop}
                radius="md"
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.03)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                }}
              >
                <L2KH5 mb={isMobile ? "sm" : "md"}>URL</L2KH5>
                <Stack gap="xs">
                  <L2KCaption variant="tertiary">Slug</L2KCaption>
                  <L2KText size="sm" style={{ wordBreak: "break-all" }}>
                    {video.slug}
                  </L2KText>
                  {video.published && (
                    <a
                      href={`/videos/${video.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "#FF9800", display: "flex", alignItems: "center", gap: "4px" }}
                    >
                      <L2KCaption variant="accent">View live page</L2KCaption>
                      <IconExternalLink size={14} />
                    </a>
                  )}
                </Stack>
              </Paper>
            </Stack>
          </Grid.Col>
        </Grid>
      </Stack>
    </>
  );
}

VideosEdit.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>;
